/**
 * CandidateAssessmentsPage
 * Route: /candidate/assessments
 *
 * Landing page after candidate login. Fetches all participations
 * (actionable + completed) and presents them in four status tabs:
 *   - Not Started (Invited, Provisioned)
 *   - In Progress (In Progress, Paused, Awaiting Client Input)
 *   - Completed (Completed, Closed)
 *   - Expired (non-completed rows whose due date has passed)
 *
 * All tabs are always visible with a row count; an empty tab renders an
 * EmptyState. The default tab is the first non-empty one in priority
 * order In Progress → Not Started → Completed → Expired, so candidates
 * land where their work is. The dashboard always renders — there is no
 * single-assessment auto-redirect.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardX,
  ClipboardList,
  Hourglass,
  CalendarX,
  Play,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  CircleCheck,
} from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { useActiveTestStore } from '../../stores/activeTestStore';
import { PageSpinner } from '../../components/PageSpinner';
import { EmptyState } from '../../components/EmptyState';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { SCENARIOS, isClientTPScenario, type ScenarioId } from '../../constants/scenarios';
import { getDueDateInfo } from '../../lib/dueDate';
import { formatLocalDateTime } from '../../lib/dateFormat';

export interface AssessmentRow {
  test: {
    id: string;
    name?: string;
    due_date?: string;
    scenario_type?: string;
  } | null;
  participant?: { id?: string; status?: string };
  scenario_type?: string;
  scenario?: { id?: string; name?: string; skip_tp?: boolean };
  status: string;
  progress: { answered: number; total: number };
  is_completed: boolean;
  completed_at?: string | null;
  tp_submitted: boolean;
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  'In Progress': 'bg-green-50 text-green-700 border-green-300',
  Paused: 'bg-amber-50 text-amber-800 border-brand-warning/50',
  Invited: 'bg-blue-50 text-blue-800 border-blue-200',
  'Awaiting Client Input': 'bg-blue-50 text-blue-800 border-blue-200',
  Provisioned: 'bg-brand-panel text-brand-steel/70 border-brand-steel/30',
  Closed: 'bg-slate-50 text-slate-600 border-slate-300',
};

const IN_PROGRESS_STATUSES = new Set(['In Progress', 'Paused', 'Awaiting Client Input']);
const NOT_STARTED_STATUSES = new Set(['Invited', 'Provisioned']);
const COMPLETED_STATUSES = new Set(['Completed', 'Closed']);

type TabKey = 'not_started' | 'in_progress' | 'completed' | 'expired';

/** Display order of the tab bar (assessment lifecycle). */
const TAB_DEFS: Array<{
  value: TabKey;
  label: string;
  empty: { icon: React.ElementType; title: string; description: string };
}> = [
  {
    value: 'not_started',
    label: 'Not Started',
    empty: {
      icon: ClipboardList,
      title: 'Nothing to start',
      description:
        'You have no new assessments waiting. Check the other tabs for your ongoing and past tests.',
    },
  },
  {
    value: 'in_progress',
    label: 'In Progress',
    empty: {
      icon: Hourglass,
      title: 'Nothing in progress',
      description: 'Assessments you have started but not finished will appear here.',
    },
  },
  {
    value: 'completed',
    label: 'Completed',
    empty: {
      icon: CheckCircle,
      title: 'No completed assessments',
      description: 'Assessments you finish will appear here.',
    },
  },
  {
    value: 'expired',
    label: 'Expired',
    empty: {
      icon: CalendarX,
      title: 'No expired assessments',
      description: 'Assessments whose due date passed before you finished will appear here.',
    },
  },
];

/** Default-tab priority: land the candidate where their work is. */
const DEFAULT_TAB_PRIORITY: TabKey[] = ['in_progress', 'not_started', 'completed', 'expired'];

function StatusBadge({ status }: { status: string }) {
  const classes =
    STATUS_BADGE_CLASSES[status] ?? 'bg-brand-panel text-brand-steel border-brand-steel/30';
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${classes}`}
    >
      {status}
    </span>
  );
}

function ctaLabelFor(row: AssessmentRow): { label: string; isResume: boolean } {
  const aqStarted = row.progress.answered > 0;
  const isResume = aqStarted || row.tp_submitted;
  return { label: isResume ? 'Continue' : 'Start', isResume };
}

/**
 * Route a candidate from the list to the right next page.
 *
 * First-timers land on the Welcome screen for the test-context debrief.
 * Returning candidates (TP done or any AQ answered) skip Welcome and go
 * straight to where they left off — TP for Sc.1-3 candidates who haven't
 * finished allocations, AQ otherwise. The AQ page already renders the
 * Sc.4/5 "waiting on your company" state, so first-timers on a still-
 * gated test also flow through Welcome → wait screen, never the list →
 * AQ shortcut.
 */
export function nextRouteFor(row: AssessmentRow): string {
  const aqStarted = row.progress.answered > 0;
  const hasProgress = aqStarted || row.tp_submitted;
  if (!hasProgress) return '/candidate/welcome';
  const skipTP = isClientTPScenario(row.scenario)
    || isClientTPScenario(row.scenario_type ?? null);
  if (row.tp_submitted || skipTP) return '/candidate/aq';
  return '/candidate/tp';
}

function scenarioAndDueLine(row: AssessmentRow, due: string | null): string {
  const scenario = row.scenario_type
    ? (SCENARIOS[row.scenario_type as ScenarioId]?.name ?? row.scenario_type)
    : '—';
  return due ? `${scenario} • Due ${due}` : scenario;
}

function CompletedCard({ row }: { row: AssessmentRow }) {
  const due = getDueDateInfo(row.test?.due_date)?.formatted ?? null;
  return (
    <Card className="border-brand-steel/50 bg-white shadow-sm opacity-85">
      <CardContent className="px-5 py-1">
        <div className="py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {row.test?.name || 'Untitled assessment'}
            </h3>
            <p className="text-xs text-gray-500 mt-1">{scenarioAndDueLine(row, due)}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-2 text-xs font-medium text-brand-success">
              <CircleCheck size={16} aria-hidden="true" className="fill-brand-success stroke-white" />
              <span>
                Completed{row.completed_at ? ` on ${formatLocalDateTime(row.completed_at)}` : ''}
              </span>
            </div>
            {/* The tab already says Completed — badge only for the distinct Closed state. */}
            {row.status === 'Closed' && <StatusBadge status={row.status} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ActionableCardProps {
  row: AssessmentRow;
  onOpen: (row: AssessmentRow) => void;
  /** In Progress tab shows the bar even before the first AQ answer. */
  showProgressWhenUnstarted: boolean;
  /**
   * Decided by the grouping that placed the row in its tab — passed down
   * (not re-derived here) so tab membership and card styling can never
   * disagree, e.g. across a midnight due-date crossover.
   */
  expired: boolean;
}

function ActionableCard({ row, onOpen, showProgressWhenUnstarted, expired }: ActionableCardProps) {
  const due = getDueDateInfo(row.test?.due_date)?.formatted ?? null;

  const { label, isResume } = ctaLabelFor(row);
  const aqStarted = row.progress.answered > 0;
  const pct =
    row.progress.total > 0
      ? Math.min(100, Math.round((row.progress.answered / row.progress.total) * 100))
      : 0;

  return (
    <Card
      className={`border-brand-steel/50 bg-white shadow-sm${expired ? ' opacity-75' : ''}`}
    >
      <CardContent className="px-5 py-1">
        <div className="py-3 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {row.test?.name || 'Untitled assessment'}
              </h3>
              <p className="text-xs text-gray-500 mt-1">{scenarioAndDueLine(row, due)}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* The Expired tab already labels these rows — no per-card badge. */}
              {!expired && <StatusBadge status={row.status} />}
              <Button
                size="sm"
                onClick={() => onOpen(row)}
                disabled={expired}
                className="inline-flex items-center gap-2"
              >
                {isResume ? (
                  <ChevronRight size={16} aria-hidden="true" />
                ) : (
                  <Play size={16} aria-hidden="true" />
                )}
                {label}
              </Button>
            </div>
          </div>

          {!expired && row.progress.total > 0 && (showProgressWhenUnstarted || aqStarted) && (
            <Progress
              value={pct}
              className="h-1.5 bg-brand-steel/10 [&>div]:bg-brand-crimson [&>div]:rounded-full rounded-full"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CandidateAssessmentsPage() {
  const navigate = useNavigate();
  const setTestId = useActiveTestStore((s) => s.setTestId);

  const [rows, setRows] = useState<AssessmentRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<AssessmentRow[]>('/api/candidate/assessments?include=all')
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load assessments');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => {
    const byTab: Record<TabKey, AssessmentRow[]> = {
      not_started: [],
      in_progress: [],
      completed: [],
      expired: [],
    };
    for (const r of rows ?? []) {
      if (!r.test?.id) continue;
      if (COMPLETED_STATUSES.has(r.status)) {
        // A finished test is never "expired", whatever its due date.
        byTab.completed.push(r);
      } else if (r.status === 'Expired') {
        // Server-derived: participant is still actionable but due_date passed.
        byTab.expired.push(r);
      } else if (IN_PROGRESS_STATUSES.has(r.status) || NOT_STARTED_STATUSES.has(r.status)) {
        if (IN_PROGRESS_STATUSES.has(r.status)) byTab.in_progress.push(r);
        else byTab.not_started.push(r);
      } else {
        // Unknown statuses render nowhere (matches the backend's exclusion
        // of "Not Invited") — warn so a future backend status can't make an
        // assessment vanish silently.
        console.warn(
          `CandidateAssessmentsPage: unknown participant status "${r.status}" — row hidden`,
          r.test.id,
        );
      }
    }
    return byTab;
  }, [rows]);

  const defaultTab =
    DEFAULT_TAB_PRIORITY.find((key) => groups[key].length > 0) ?? 'not_started';
  const hasActionable = groups.not_started.length + groups.in_progress.length > 0;
  // Count only rows that appear in a tab, so this always matches the tab
  // counts summed (unknown statuses and null tests are excluded from both).
  const totalCount =
    groups.not_started.length +
    groups.in_progress.length +
    groups.completed.length +
    groups.expired.length;

  const handleOpen = (row: AssessmentRow) => {
    if (!row.test?.id) return;
    setTestId(row.test.id);
    navigate(nextRouteFor(row));
  };

  if (isLoading) {
    return <PageSpinner label="Loading assessments" />;
  }

  if (error) {
    return (
      <main className="max-w-3xl mx-auto py-12 px-4">
        <div
          role="alert"
          className="p-4 bg-red-50 border border-brand-error/40 rounded-lg text-sm text-brand-error"
        >
          {error}
        </div>
      </main>
    );
  }

  // Gate on the grouped total, not rows.length — a response containing only
  // unrenderable rows (unknown status, null test) must not render a "0
  // assessments" tab UI.
  if (!rows || totalCount === 0) {
    return (
      <main className="max-w-3xl mx-auto py-12 px-4">
        <EmptyState
          icon={ClipboardX}
          title="No assessments"
          description="You don't have any assessments right now. Your test administrator will let you know when one is ready for you."
        />
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto py-12 px-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Your assessments</h1>
      <p className="text-sm text-brand-steel mb-6">
        {`${totalCount} assessment${totalCount === 1 ? ' is' : 's are'} ${
          hasActionable ? 'assigned to you' : 'in your history'
        }. Browse ${totalCount === 1 ? 'it' : 'them'} by status using the tabs below.`}
      </p>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-transparent rounded-none p-0 h-auto gap-2 mb-3 w-full justify-start flex-wrap">
          {TAB_DEFS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="px-4 py-1.5 text-sm font-medium rounded-lg border border-brand-steel/50 bg-white text-gray-700 transition-colors data-[state=active]:bg-brand-navy data-[state=active]:text-white data-[state=active]:border-brand-navy data-[state=active]:shadow-none"
            >
              {tab.label} ({groups[tab.value].length})
            </TabsTrigger>
          ))}
        </TabsList>

        {TAB_DEFS.map((tab) => (
          <TabsContent key={tab.value} value={tab.value}>
            {groups[tab.value].length === 0 ? (
              <EmptyState
                icon={tab.empty.icon}
                title={tab.empty.title}
                description={tab.empty.description}
              />
            ) : (
              <div className="space-y-3">
                {tab.value === 'expired' && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-brand-error/40 rounded-lg text-xs text-brand-error">
                    <AlertTriangle size={16} aria-hidden="true" className="shrink-0" />
                    <span>
                      The due date for {groups.expired.length === 1 ? 'this assessment' : 'these assessments'} has
                      passed. Contact your administrator if you need an extension.
                    </span>
                  </div>
                )}
                {groups[tab.value].map((row) =>
                  tab.value === 'completed' ? (
                    <CompletedCard
                      key={`${row.participant?.id ?? ''}-${row.test!.id}`}
                      row={row}
                    />
                  ) : (
                    <ActionableCard
                      key={`${row.participant?.id ?? ''}-${row.test!.id}`}
                      row={row}
                      onOpen={handleOpen}
                      showProgressWhenUnstarted={tab.value === 'in_progress'}
                      expired={tab.value === 'expired'}
                    />
                  ),
                )}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </main>
  );
}

export default CandidateAssessmentsPage;
